import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ScoringSystemActions } from '../actions/actions-type.action';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import { NotificationsService } from '@shared/services/notifications.service';
import { BadResponseWithError } from '@shared/models/common.interface';
import { ScoringSystemService } from '../../services/scoring-system.service';

@Injectable({ providedIn: 'root' })
export class ScoringSystemsEffect {
    fetch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ScoringSystemActions.fetchScoringSystemsRequests),
            switchMap(() =>
                this.service.fetchScoringSystems().pipe(
                    map((response) =>
                        ScoringSystemActions.fetchScoringSystemsSuccess({
                            payload: response,
                        })
                    ),
                    catchError((response: any) => {
                        this.notifications.showError(response.message);
                        return of(
                            ScoringSystemActions.fetchScoringSystemFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ScoringSystemActions.updateScoringSystemRequests),
            switchMap(({ body }) =>
                this.service.updateScoringSystem(body).pipe(
                    map(() => {
                        this.notifications.showSuccessMessage(
                            'Scoring System updated successfully'
                        );
                        return ScoringSystemActions.updateScoringSystemSuccess({
                            body,
                        });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notifications.showError(response.error.message);
                        return of(
                            ScoringSystemActions.updateScoringSystemFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    fetchScoringGamePlatform$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ScoringSystemActions.fetchScoringGamePlatformRequests),
            switchMap(({ gameid }) =>
                this.service.fetchScoringSettingsGamePlatforms(gameid).pipe(
                    map((response) =>
                        ScoringSystemActions.fetchScoringGamePlatformSuccess({
                            payload: response,
                        })
                    ),
                    catchError((response: any) => {
                        this.notifications.showError(response.message);
                        return of(
                            ScoringSystemActions.fetchScoringGamePlatformFailure(
                                {
                                    payload: response,
                                }
                            )
                        );
                    })
                )
            )
        )
    );

    updateScoringGamePlatform$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ScoringSystemActions.updateScoringGamePlatformRequests),
            switchMap(({ gameid, body }) =>
                this.service
                    .updateScoringSettingsGamePlatforms(gameid, body)
                    .pipe(
                        map(() => {
                            this.notifications.showSuccessMessage(
                                'Scoring Game Platform updated successfully'
                            );
                            return ScoringSystemActions.updateScoringGamePlatformSuccess(
                                { body }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notifications.showError(
                                response.error.message
                            );
                            return of(
                                ScoringSystemActions.updateScoringGamePlatformFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private service: ScoringSystemService,
        private notifications: NotificationsService
    ) {}
}
